/** XP rules, streak, daily goal and weekly totals — all derived from xp_events. */
import { addDays, weekStart, type IsoDay } from './date.ts';
import { CardState, Rating } from './fsrs.ts';

/** XP is only granted for real recall: a correct exercise answer or a successful review. */
export const XP = {
  exerciseCorrect: 10,
  reviewRecalled: 2,
} as const;

/** Number of flags in the daily-goal garland. */
export const GARLAND_FLAGS = 10;

export function xpForExercise(correct: boolean): number {
  return correct ? XP.exerciseCorrect : 0;
}

/**
 * Reviews pay only for recall across a real interval, i.e. a card that was in Review:
 * new, learning and relearning steps repeat within minutes, so paying for them would turn
 * the session into an XP farm. Mirrors the server's award_xp() check (log state = Review).
 */
export function xpForReview(rating: Rating, stateBefore: CardState): number {
  if (rating === Rating.Again || stateBefore !== CardState.Review) return 0;
  return XP.reviewRecalled;
}

export interface XpEvent {
  day: IsoDay;
  amount: number;
}

export function xpByDay(events: readonly XpEvent[]): Map<IsoDay, number> {
  const totals = new Map<IsoDay, number>();
  for (const e of events) totals.set(e.day, (totals.get(e.day) ?? 0) + e.amount);
  return totals;
}

/**
 * Consecutive days with XP, ending today — or yesterday, so the streak is not shown as
 * broken before the user has had a chance to study today.
 */
export function streak(events: readonly XpEvent[], today: IsoDay): number {
  const totals = xpByDay(events);
  const active = (day: IsoDay) => (totals.get(day) ?? 0) > 0;
  let day = active(today) ? today : addDays(today, -1);
  let count = 0;
  while (active(day)) {
    count += 1;
    day = addDays(day, -1);
  }
  return count;
}

export function xpOnDay(events: readonly XpEvent[], day: IsoDay): number {
  return xpByDay(events).get(day) ?? 0;
}

export function weeklyXp(events: readonly XpEvent[], today: IsoDay): number {
  const start = weekStart(today);
  return events
    .filter((e) => e.day >= start && e.day <= today)
    .reduce((sum, e) => sum + e.amount, 0);
}

export interface DailyGoal {
  earned: number;
  goal: number;
  /** Filled garland flags, 0..GARLAND_FLAGS. */
  flags: number;
  met: boolean;
}

export function dailyGoal(events: readonly XpEvent[], today: IsoDay, goal: number): DailyGoal {
  const earned = xpOnDay(events, today);
  const safeGoal = Math.max(1, goal);
  const flags = Math.min(GARLAND_FLAGS, Math.floor((earned / safeGoal) * GARLAND_FLAGS));
  return { earned, goal: safeGoal, flags, met: earned >= safeGoal };
}
